import { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Axios from '@/shared/api/axios';
import Loading from '@/components/ui/Loading';
import StatusModal from '@/components/ui/StatusModal';
import Pagination from '@/components/ui/Pagination';
import MediaThumbnail from '@/features/idol/components/MediaThumbnail';
import { useStreamPolling, extractMediaUnreadyVideos } from '@/hooks/useStreamPolling';

const LIMIT = 12;

const TabMedia = ({ viewIdolId }) => {
  const { t } = useTranslation();

  const idolId = useSelector((state) => state.user.idolId);

  const statusModalRef = useRef(null);
  const shouldScrollOnLoad = useRef(false);

  const [isLoading, setIsLoading] = useState(false);
  const [list, setList] = useState(null);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentStatusCode, setCurrentStatusCode] = useState('');

  const getList = async (page) => {
    setIsLoading(true);
    try {
      const res = await Axios.post('/post/get-media-list', {
        idol_id: idolId,
        view_idol_id: viewIdolId,
        page,
        limit: LIMIT,
      });
      switch (res.data.status_code) {
        case 'SYSTEM_1000':
          setList(res.data.data);
          setTotal(res.data.total);
          break;
        default:
          setCurrentStatusCode(res.data.status_code);
          statusModalRef.current?.show();
          break;
      }
    } catch (error) {
      // NETWORK_ERROR 由全域 ServerErrorPage 處理，不另外顯示 modal
      if (error.httpErrorCode && error.httpErrorCode !== 'NETWORK_ERROR') {
        setCurrentStatusCode(error.httpErrorCode);
        statusModalRef.current?.show();
      } else if (!error.httpErrorCode) {
        console.error('Get idol media list error:', error);
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (viewIdolId) {
      setCurrentPage(1);
      shouldScrollOnLoad.current = true;
      getList(1);
    }
  }, [viewIdolId]);

  // 切到此 tab 當下資料還沒回來，內容比 navbar 下方預期位置短，此時捲動會被瀏覽器 clamp 回頂端；
  // 要等資料進來、grid 實際撐開高度後才能正確捲到 tab 列（分頁換頁不需要，故用 ref 只在初次載入時觸發一次）
  useEffect(() => {
    if (list !== null && shouldScrollOnLoad.current) {
      shouldScrollOnLoad.current = false;
      document.querySelector('.idol-tab-bar')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [list]);

  const handlePageChange = (page) => {
    setCurrentPage(page);
    getList(page);
  };

  const handleStreamStatusChange = (changedItems) => {
    setList((prev) =>
      prev.map((item) => {
        const changed = changedItems.find((c) => c.stream_uid === item.stream_uid);
        return changed ? { ...item, stream_status: changed.stream_status } : item;
      })
    );
  };

  const { videos: unreadyVideos, createdAt } = extractMediaUnreadyVideos(list);
  useStreamPolling({
    endpoint: '/post/get-stream-status',
    unreadyVideos,
    createdAt,
    onStatusChange: handleStreamStatusChange,
  });

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <>
      {list === null ? (
        <div className="idol-tab-loading-placeholder" />
      ) : list.length > 0 ? (
        <div className="media-grid mb-4">
          {list.map((item) => (
            <MediaThumbnail key={item.id} media={item} />
          ))}
        </div>
      ) : (
        <p className="mb-0 text-center py-4">{t('no_media_data')}</p>
      )}

      {totalPages > 1 && (
        <div className="d-flex justify-content-center">
          <Pagination
            total={total}
            limit={LIMIT}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            scrollMode="container"
            scrollContainerSelector=".idol-tab-bar"
          />
        </div>
      )}

      <StatusModal ref={statusModalRef} statusCode={currentStatusCode} />
      {isLoading && <Loading />}
    </>
  );
};

export default TabMedia;
